import React, { useState } from "react";
import * as yup from "yup";
import Select from "react-select";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import useAxios from "../../hooks/useAxios";
import { LoadingButton } from "../LoadingButtons";
import "react-datepicker/dist/react-datepicker.css";
import { yupResolver } from "@hookform/resolvers/yup";
import { Navigate, useParams } from "react-router-dom";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { alertSuccess, alertWarning } from "../CustomAlerts/AlertToast";
import { Row, Col, Form, Badge, Button, FormGroup } from "react-bootstrap";

const schema = yup.object({
  receiver: yup.string().required("Who received the delivery?"),
  deliveryDate: yup.string().nullable().required("Date of delivery required"),
  deliveryTime: yup.string().required("Time of delivery required"),
  items: yup.array().of(
    yup.object({
      item_id: yup.string(),
      quantity_recieved: yup.string().required("Quantity is required"),
    })
  ),
});

const SupplyRecordForm = ({ kitItems, logisticsStaff, purchaseOrder }) => {
  const { post } = useAxios();
  let { purchaseOrderId } = useParams();
  const [loading, setLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [itemsErrors, setItemsErrors] = useState([]);
  const [fullyDeliveredItems, setFullyDeliveredItems] = useState([]);
  const [partiallyDeliveredItems, setPartiallyDeliveredItems] = useState([]);

  const notFullyDelivered = (fields) => {
    if (partiallyDeliveredItems.length) {
      return partiallyDeliveredItems;
    }

    const newPartiallyDeliveredItems = [...partiallyDeliveredItems];
    const newFullyDeliveredItems = [...fullyDeliveredItems];

    fields.forEach((field) => {
      const undeliveredQuantity = field?.quantity - field?.quantity_delivered;
      const isPartiallyDelivered = undeliveredQuantity > 0;
      if (isPartiallyDelivered) {
        newPartiallyDeliveredItems.push(field);
      } else {
        newFullyDeliveredItems.push(field);
      }
    });

    setPartiallyDeliveredItems(newPartiallyDeliveredItems);
    setFullyDeliveredItems(newFullyDeliveredItems);
    return newPartiallyDeliveredItems;
  };

  const {
    control,
    setValue,
    register,
    setError,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    reValidateMode: "onChange",
    defaultValues: {
      mode: "onBlur",
      receiver: "",
      deliveryDate: "",
      deliveryTime: "",
      items: notFullyDelivered(kitItems),
    },
  });

  const { fields } = useFieldArray({
    control,
    name: "items",
  });

  const collectorOptions = Object.keys(logisticsStaff).map((staff) => ({
    value: staff,
    label: logisticsStaff[staff],
  }));

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const itemsArray = data.items;

      const itemsData = itemsArray.map((itemProperty) => {
        return {
          item_id: Number(itemProperty.kit_item.id),
          quantity_delivered: Number(itemProperty.quantity_recieved),
        };
      });

      fullyDeliveredItems.forEach((item) => {
        itemsData.push({
          item_id: item.kit_item.id,
          quantity_delivered: 0,
        });
      });

      const supplyRecordData = {
        receive_by_employee_code: data.receiver,

        date_received: format(new Date(data.deliveryDate), "yyyy-MM-dd", {
          awareOfUnicodeTokens: true,
        }),

        time_received: format(new Date(data.deliveryTime), "HH:mm:ss", {
          awareOfUnicodeTokens: true,
        }),
        items: itemsData,
      };

      await post(
        `purchaseorder/${purchaseOrderId}/supply_record`,
        supplyRecordData
      );

      setIsSaved(true);

      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      alertSuccess("Supply record of order successfully saved!", null, 5000);
    } catch (error) {
      setLoading(false);
      if (error.response.status === 401) {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        alertWarning(
          "Sorry, something went wrong and your request was unsuccessful!",
          null,
          5000
        );
        const handleServerErrors = (errors, setError) => {
          if (errors) {
            const serverValidation = error.response.data.errors;
            return Object.keys(serverValidation).map((key) => {
              setError(key, {
                shouldFocus: true,
                type: "manual",
                message: serverValidation[key][0],
              });
            });
          }
        };
        handleServerErrors(error, setError);
        return;
      }
      if (error.response.status === 500) {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        alertWarning(
          "Something went wrong with the server. Please Try Again!",
          null,
          5000
        );
      } else {
        const hasItemsError = !!error?.response?.data?.items_errors;

        const errorMessage = hasItemsError
          ? "Please correct the quantity entered for the indicated items"
          : "Items completed supplied";

        setItemsErrors(error?.response?.data?.items_errors ?? []);
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        alertWarning(errorMessage, null, 5000);
      }
    }
  };

  return (
    <>
      {isSaved && (
        <Navigate
          to={`/purchase-order/view/${purchaseOrderId}`}
          replace={true}
        />
      )}

      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="delivery-details mt-4 mb-4 border">
          <Form.Label className="mt-2 p-2">DELIVERY DETAILS</Form.Label>

          <div className="recipient-name ms-2 me-2 mb-2 p-2">
            <Form.Group controlId="recipientName">
              <Form.Label className="required">RECIPIENT NAME</Form.Label>
              <Controller
                name="receiver"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    className="text-dark"
                    options={collectorOptions}
                    onChange={(event) => {
                      setValue("receiver", event.value, {
                        shouldValidate: true,
                      });
                    }}
                    value={field.label}
                    placeholder="Who collected PO items delivery"
                  />
                )}
              />

              {!!errors.receiver && (
                <p>
                  <Badge bg="danger">{errors.receiver?.message}</Badge>
                </p>
              )}
            </Form.Group>
          </div>

          <Row className="delivery-date-time ms-0 me-0 p-2">
            <Col className="delivery-date mt-2 mb-3" md={6}>
              <FormGroup controlId="deliveryDate">
                <Form.Label className="required">DATE</Form.Label>
                <Controller
                  name="deliveryDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      isClearable
                      strictParsing
                      showYearDropdown
                      showMonthDropdown
                      minDate={new Date(purchaseOrder?.created_at)}
                      maxDate={new Date()}
                      closeOnScroll={true}
                      dropdownMode="select"
                      selected={field.value}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      onChange={(date) => {
                        setValue("deliveryDate", date);
                      }}
                      placeholderText="Enter delivery date"
                    />
                  )}
                />

                {!!errors.deliveryDate && (
                  <p>
                    <Badge bg="danger">{errors.deliveryDate?.message}</Badge>
                  </p>
                )}
              </FormGroup>
            </Col>

            <Col className="delivery-time mt-2 mb-2" md={6}>
              <FormGroup controlId="deliveryTime">
                <Form.Label className="required">TIME</Form.Label>
                <Controller
                  name="deliveryTime"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      isClearable
                      showTimeSelect
                      timeIntervals={1}
                      timeCaption="Time"
                      showTimeSelectOnly
                      closeOnScroll={true}
                      dateFormat="HH:mm:ss"
                      selected={field.value}
                      className="form-control"
                      onChange={(date) => {
                        setValue("deliveryTime", date);
                      }}
                      placeholderText="Enter delivery time"
                    />
                  )}
                />

                {!!errors.deliveryTime && (
                  <p>
                    <Badge bg="danger">{errors.deliveryTime?.message}</Badge>
                  </p>
                )}
              </FormGroup>
            </Col>
          </Row>
        </div>

        {!!itemsErrors?.length && (
          <div className="alert alert-danger alert-important">
            {itemsErrors.map((error) => (
              <ul key={error}>
                <li>{error}</li>
              </ul>
            ))}
          </div>
        )}

        <div className="kitItem-details mt-4 mb-4 border">
          <Form.Label className="mt-2 p-2">GUARD KIT ITEM DETAILS</Form.Label>
          {fields.map((field, index) => (
            <Row
              key={field.id}
              className="d-flex justify-content-between ms-0 me-0 mb-1 p-2 "
            >
              <Col className="kit-item mb-3" md={8}>
                <Form.Group controlId="kitItem ">
                  <Form.Label>KIT ITEM</Form.Label>
                  <Form.Control
                    disabled
                    type="text"
                    name={`items.${index}.item_id`}
                    defaultValue={`${field?.kit_item?.name}`}
                  />
                </Form.Group>
              </Col>

              <Col className="kit-item-quantity mb-3" md={4}>
                <Form.Label className="required">QUANTITY DELIVERED</Form.Label>
                <Form.Control
                  min={0}
                  key={index}
                  type="number"
                  max={field?.quantity - field?.quantity_delivered}
                  data-quantity-recieved-index={index}
                  placeholder="Enter quantity received"
                  {...register(`items.${index}.quantity_recieved`)}
                  onChange={(event) => {
                    setValue(
                      `items.${index}.quantity_recieved`,
                      event.target.value,
                      {
                        shouldValidate: true,
                      }
                    );
                  }}
                  defaultValue={0}
                />

                {!!errors.quantity_recieved && (
                  <p>
                    <Badge bg="danger">
                      {errors.items?.[index].quantity_recieved?.message}
                    </Badge>
                  </p>
                )}
              </Col>

              <hr className="mt-4" />
            </Row>
          ))}
        </div>

        <div className="submit col-md-12 text-center mb-3">
          {loading ? (
            <LoadingButton />
          ) : (
            <Button
              type="submit"
              variant="success"
              disabled={!!loading || !isDirty}
            >
              Submit
            </Button>
          )}
        </div>
      </Form>
    </>
  );
};

export default SupplyRecordForm;
